import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { CircleSlash, Clock, LogOut, MailX, UserCheck } from 'lucide-react'
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { api } from '@/api'
import { ApiError } from '@/api/client'
import { acceptInvite, invitePreviewQuery, logoSrc, meQuery } from '@/api/queries'
import type { InvitePreview, RegisterResponse } from '@/api/types'
import { signOut } from '@/app/session'
import { errorText } from '@/domain/errors'
import { nicknameFrom, randomDigits } from '@/domain/invites'
import { formatDate, t } from '@/i18n'
import { AuthFrame } from '@/ui/auth-frame'
import { Button } from '@/ui/button'
import { CompanyLogo } from '@/ui/company-logo'
import { Field, PasswordField } from '@/ui/field'
import { InlineBanner } from '@/ui/inline-banner'
import { EmptyState } from '@/ui/page'
import { SettingsRow, SettingsSection } from '@/ui/settings'
import { LoadingBlock } from '@/ui/spinner'

/// `?accept=1`: came back from signing in for this invitation — accept it
/// straight away instead of asking twice.
type InviteSearch = { accept?: boolean }

export const Route = createFileRoute('/invite/$token')({
  validateSearch: (search: Record<string, unknown>): InviteSearch =>
    search['accept'] === true || search['accept'] === 'true' || search['accept'] === 1 ? { accept: true } : {},
  component: InvitePage,
})

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/// What went wrong with an accept, as the page shows it.
type Failure =
  | Readonly<{ kind: 'mismatch' }>
  | Readonly<{ kind: 'stale' }>
  | Readonly<{ kind: 'member' }>
  | Readonly<{ kind: 'other'; message: string }>

const failureOf = (error: unknown): Failure => {
  if (error instanceof ApiError) {
    if (error.code === 'invite_email_mismatch') return { kind: 'mismatch' }
    if (error.code === 'invite_stale') return { kind: 'stale' }
    if (error.code === 'already_member') return { kind: 'member' }
  }
  return { kind: 'other', message: errorText(error) }
}

/// /invite/{token} — the page an invitation link opens. Public: the person
/// may have no account yet. It shows whose invitation it is (company, role,
/// who invited, the address masked) and then the one way forward: sign in
/// or create an account with that address, or — signed in — accept. After
/// accepting, straight into the company.
function InvitePage() {
  const { token } = Route.useParams()
  const { accept: autoAccept } = Route.useSearch()
  const preview = useQuery(invitePreviewQuery(token))

  return (
    <AuthFrame width="medium">
      <div className="mx-auto w-full max-w-[420px]">
        {preview.isPending ? (
          <LoadingBlock label={t.app.loading} />
        ) : preview.isError ? (
          preview.error instanceof ApiError && preview.error.status === 404 ? (
            <EmptyState icon={<MailX />} title={t.invitePage.unknownTitle} message={t.invitePage.unknownMessage} />
          ) : (
            <InlineBanner
              kind="error"
              message={errorText(preview.error)}
              action={{ label: t.app.retry, onClick: () => void preview.refetch() }}
            />
          )
        ) : (
          <Invitation token={token} preview={preview.data} autoAccept={Boolean(autoAccept)} />
        )}
      </div>
    </AuthFrame>
  )
}

function Invitation({
  token,
  preview,
  autoAccept,
}: {
  token: string
  preview: InvitePreview
  autoAccept: boolean
}) {
  const signedIn = api.hasSession()
  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col items-center gap-5 text-center">
        <CompanyLogo
          name={preview.organization.name}
          src={logoSrc(preview.organization.logoUrl)}
          size="xl"
          className="size-[72px] rounded-[18px] text-[24px]"
        />
        <div className="flex flex-col gap-2">
          <h1 className="text-title font-bold tracking-[-0.015em] text-balance text-text">{t.invitePage.title}</h1>
          <p className="text-callout text-pretty text-text-2">{t.invitePage.lead(preview.organization.name)}</p>
        </div>
      </div>

      <SettingsSection>
        {preview.roleName ? <SettingsRow title={t.invitePage.role} value={preview.roleName} /> : null}
        {preview.invitedByName ? (
          <SettingsRow title={t.invitePage.invitedBy} value={preview.invitedByName} />
        ) : null}
        <SettingsRow title={t.invitePage.email} value={preview.email} />
        {preview.status === 'pending' ? (
          <SettingsRow title={t.invitePage.expires} value={formatDate(preview.expiresAt)} />
        ) : null}
      </SettingsSection>

      {preview.status === 'pending' ? (
        signedIn ? (
          <SignedIn token={token} preview={preview} autoAccept={autoAccept} />
        ) : (
          <SignedOut token={token} preview={preview} />
        )
      ) : (
        <Closed status={preview.status} signedIn={signedIn} />
      )}
    </div>
  )
}

/// Expired, revoked or already used: say which, and what to do.
function Closed({ status, signedIn }: { status: InvitePreview['status']; signedIn: boolean }) {
  const navigate = useNavigate()
  const [icon, title, message]: [ReactNode, string, string] =
    status === 'expired'
      ? [<Clock key="i" />, t.invitePage.expiredTitle, t.invitePage.expiredMessage]
      : status === 'revoked'
        ? [<CircleSlash key="i" />, t.invitePage.revokedTitle, t.invitePage.revokedMessage]
        : [<UserCheck key="i" />, t.invitePage.acceptedTitle, t.invitePage.acceptedMessage]
  return (
    <EmptyState
      className="py-4"
      icon={icon}
      title={title}
      message={message}
      action={
        status === 'accepted' ? (
          <Button variant="secondary" onClick={() => void navigate(signedIn ? { to: '/' } : { to: '/sign-in' })}>
            {t.invitePage.toPanel}
          </Button>
        ) : undefined
      }
    />
  )
}

/// Accepting lands in the company: the cached company list is the one from
/// before joining, so everything is thrown away first.
const useLandInCompany = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  return async (orgId: string) => {
    queryClient.clear()
    await navigate({ to: '/c/$orgId', params: { orgId }, replace: true })
  }
}

function SignedIn({
  token,
  preview,
  autoAccept,
  initialFailure = null,
}: {
  token: string
  preview: InvitePreview
  autoAccept: boolean
  /// An accept that already failed — right after creating an account.
  initialFailure?: Failure | null
}) {
  const me = useQuery(meQuery())
  const land = useLandInCompany()
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<Failure | null>(initialFailure)
  const started = useRef(false)

  const accept = async () => {
    setBusy(true)
    setFailure(null)
    try {
      const res = await acceptInvite(token)
      await land(res.orgId)
    } catch (error) {
      setFailure(failureOf(error))
      setBusy(false)
    }
  }

  useEffect(() => {
    // Once: StrictMode runs effects twice, and a second accept would answer
    // «already used» to the person who just used it.
    if (!autoAccept || started.current) return
    started.current = true
    void accept()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAccept])

  if (failure?.kind === 'mismatch') {
    return (
      <div className="flex flex-col gap-4">
        <InlineBanner
          kind="error"
          title={t.invitePage.mismatchTitle}
          message={t.invitePage.mismatchMessage(preview.email, me.data?.email ?? '—')}
        />
        <Button
          variant="primary"
          className="w-full sm:h-11"
          icon={<LogOut className="size-[18px]" />}
          onClick={() => void signOut(`/invite/${token}`)}
        >
          {t.invitePage.signOut}
        </Button>
      </div>
    )
  }

  if (failure?.kind === 'member') {
    return (
      <EmptyState
        className="py-4"
        icon={<UserCheck />}
        title={t.invitePage.alreadyMemberTitle}
        action={
          <Button variant="secondary" onClick={() => window.location.assign('/')}>
            {t.invitePage.toPanel}
          </Button>
        }
      />
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {me.data ? (
        <p className="text-center text-callout text-text-2">{t.invitePage.signedInAs(me.data.email)}</p>
      ) : null}
      {failure?.kind === 'stale' ? (
        <InlineBanner kind="error" title={t.invitePage.staleTitle} message={t.invitePage.staleMessage} />
      ) : failure?.kind === 'other' ? (
        <InlineBanner kind="error" message={failure.message} />
      ) : null}
      <Button
        variant="primary"
        busy={busy}
        className="w-full sm:h-11"
        onClick={() => void accept()}
      >
        {busy ? t.invitePage.accepting : t.invitePage.accept}
      </Button>
      <Button variant="quiet" className="self-center" onClick={() => void signOut(`/invite/${token}`)}>
        {t.invitePage.signOut}
      </Button>
    </div>
  )
}

/// Signed out: create an account right here (most invited staff have none),
/// or sign in with an existing one — both come back and accept.
function SignedOut({ token, preview }: { token: string; preview: InvitePreview }) {
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)

  if (creating) return <SignUp token={token} preview={preview} onBack={() => setCreating(false)} />

  return (
    <div className="flex flex-col gap-4">
      <div className="text-center">
        <h2 className="text-heading font-bold text-text">{t.invitePage.signInTitle}</h2>
        <p className="mt-1 text-callout text-pretty text-text-2">{t.invitePage.signInMessage}</p>
      </div>
      <Button variant="primary" className="w-full sm:h-11" onClick={() => setCreating(true)}>
        {t.invitePage.signUp}
      </Button>
      <Button
        variant="secondary"
        className="w-full sm:h-11"
        onClick={() => void navigate({ to: '/sign-in', search: { invite: token } })}
      >
        {t.invitePage.signIn}
      </Button>
    </div>
  )
}

/// services/auth's `POST /auth/register` wants a nickname; staff never see
/// it here, so one is made from the address. Taken or refused by the
/// profanity filter → another try with other digits, then a plain one.
const register = async (input: { name: string; email: string; password: string }): Promise<RegisterResponse> => {
  const attempts = [nicknameFrom(input.email, randomDigits(4)), nicknameFrom(input.email, randomDigits(6)), nicknameFrom('', randomDigits(6))]
  const tryNext = async (left: readonly string[]): Promise<RegisterResponse> => {
    const [nickname, ...rest] = left
    try {
      return await api.request<RegisterResponse>('/auth/register', {
        body: { ...input, nickname },
        skipAuth: true,
      })
    } catch (error) {
      const nicknameRefused =
        error instanceof ApiError && (error.code === 'nickname_taken' || error.code === 'nickname_not_allowed')
      if (nicknameRefused && rest.length > 0) return tryNext(rest)
      throw error
    }
  }
  return tryNext(attempts)
}

function SignUp({ token, preview, onBack }: { token: string; preview: InvitePreview; onBack: () => void }) {
  const navigate = useNavigate()
  const land = useLandInCompany()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<'name' | 'email' | 'password', string>>>({})
  const [serverError, setServerError] = useState<string | null>(null)
  const [emailTaken, setEmailTaken] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<Failure | null>(null)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (busy) return
    const errors = {
      ...(name.trim() === '' ? { name: t.invitePage.signUpForm.errors.emptyName } : {}),
      ...(email.trim() === ''
        ? { email: t.signIn.errors.emptyEmail }
        : EMAIL.test(email.trim())
          ? {}
          : { email: t.signIn.errors.badEmail }),
      ...(password.length < 8 ? { password: t.invitePage.signUpForm.errors.shortPassword } : {}),
    }
    setFieldErrors(errors)
    if (Object.keys(errors).length > 0) return
    setBusy(true)
    setServerError(null)
    setEmailTaken(false)
    setFailure(null)
    try {
      const account = await register({ name: name.trim(), email: email.trim(), password })
      api.setTokens(account)
    } catch (error) {
      if (error instanceof ApiError && error.code === 'email_taken') {
        setEmailTaken(true)
        setServerError(t.invitePage.signUpForm.errors.emailTaken)
      } else {
        setServerError(error instanceof ApiError && error.status === 400 ? t.invitePage.signUpForm.errors.failed : errorText(error))
      }
      setBusy(false)
      return
    }
    // The account exists and is signed in; from here it is the same accept
    // as for anyone signed in.
    try {
      const res = await acceptInvite(token)
      await land(res.orgId)
    } catch (error) {
      setFailure(failureOf(error))
      setBusy(false)
    }
  }

  if (failure) {
    // Signed in now: the signed-in view explains a mismatch and offers the rest.
    return <SignedIn token={token} preview={preview} autoAccept={false} initialFailure={failure} />
  }

  return (
    <form onSubmit={(event) => void submit(event)} noValidate className="flex flex-col gap-4">
      <div className="text-center">
        <h2 className="text-heading font-bold text-text">{t.invitePage.signUpForm.title}</h2>
        <p className="mt-1 text-callout text-pretty text-text-2">{t.invitePage.signUpForm.message}</p>
      </div>
      <Field
        label={t.invitePage.signUpForm.name}
        name="name"
        autoComplete="name"
        autoFocus
        placeholder={t.invitePage.signUpForm.namePlaceholder}
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={fieldErrors.name}
      />
      <Field
        label={t.invitePage.signUpForm.email}
        type="email"
        name="email"
        autoComplete="username"
        inputMode="email"
        placeholder={t.signIn.emailPlaceholder}
        hint={t.invitePage.signUpForm.emailHint}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={fieldErrors.email}
      />
      <PasswordField
        label={t.invitePage.signUpForm.password}
        name="password"
        autoComplete="new-password"
        placeholder={t.invitePage.signUpForm.passwordPlaceholder}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={fieldErrors.password}
      />
      {serverError ? (
        <InlineBanner
          kind="error"
          message={serverError}
          action={
            emailTaken
              ? {
                  label: t.invitePage.signIn,
                  onClick: () => void navigate({ to: '/sign-in', search: { invite: token } }),
                }
              : undefined
          }
        />
      ) : null}
      <Button type="submit" variant="primary" busy={busy} className="mt-1 w-full sm:h-11">
        {busy ? t.invitePage.signUpForm.submitting : t.invitePage.signUpForm.submit}
      </Button>
      <Button variant="quiet" className="self-center" onClick={onBack}>
        {t.app.back}
      </Button>
    </form>
  )
}
